// Tabs component for UI Component Library
// Created as part of ENG2-1691: Phase 3 - Ticket 3.5

import React from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { tokens } from '../../../styles/tokens'
import { TabsProps, TabProps, TabPanelProps, TabItem } from './tabs.types'

// Styled tabs container
const TabsContainer = styled(Box)({
  display: 'flex',
  borderBottom: `1px solid ${tokens.colors.grey[200]}`,
})

// Styled individual tab
const StyledTab = styled(Box, {
  shouldForwardProp: (prop) => !['active', 'disabled'].includes(prop as string),
})<{ active?: boolean; disabled?: boolean }>(({ active, disabled }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '12px 20px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  transition: `all ${tokens.transitions.fast}`,
  position: 'relative',
  color: disabled
    ? tokens.colors.grey[400]
    : active
    ? tokens.colors.primary[600]
    : tokens.colors.grey[500],
  opacity: disabled ? 0.6 : 1,

  '&:hover': disabled
    ? {}
    : {
        color: active ? tokens.colors.primary[600] : tokens.colors.grey[800],
      },

  // Active indicator
  '&::after': active
    ? {
        content: '""',
        position: 'absolute',
        bottom: -1,
        left: 0,
        right: 0,
        height: 2,
        backgroundColor: tokens.colors.primary[600],
        borderRadius: '2px 2px 0 0',
      }
    : {},
}))

// Styled tab count badge
const TabCount = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 20,
  height: 20,
  padding: '0 6px',
  borderRadius: 10,
  fontSize: 11,
  fontWeight: 600,
  backgroundColor: active ? tokens.colors.primary[100] : tokens.colors.grey[200],
  color: active ? tokens.colors.primary[700] : tokens.colors.grey[600],
}))

// Individual Tab component
const Tab: React.FC<TabProps> = ({ item, active, onClick }) => {
  return (
    <StyledTab
      active={active}
      disabled={item.disabled}
      onClick={item.disabled ? undefined : onClick}
      role="tab"
      aria-selected={active}
      aria-disabled={item.disabled}
      tabIndex={item.disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (!item.disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          onClick()
        }
      }}
    >
      {item.icon && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            fontSize: 14,
          }}
        >
          {item.icon}
        </Box>
      )}
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 600,
          lineHeight: 1,
        }}
      >
        {item.label}
      </Typography>
      {item.count !== undefined && (
        <TabCount active={active}>{item.count}</TabCount>
      )}
    </StyledTab>
  )
}

// Main Tabs component
export const Tabs: React.FC<TabsProps> = ({
  tabs,
  activeTab,
  onChange,
  sx,
  className,
}) => {
  return (
    <TabsContainer sx={sx} className={className} role="tablist">
      {tabs.map((tab) => (
        <Tab
          key={tab.id}
          item={tab}
          active={activeTab === tab.id}
          onClick={() => onChange(tab.id)}
        />
      ))}
    </TabsContainer>
  )
}

// TabPanel component
export const TabPanel: React.FC<TabPanelProps> = ({
  id,
  activeTab,
  children,
  sx,
}) => {
  if (activeTab !== id) {
    return null
  }

  return (
    <Box
      role="tabpanel"
      id={`tabpanel-${id}`}
      aria-labelledby={`tab-${id}`}
      sx={sx}
    >
      {children}
    </Box>
  )
}

Tabs.displayName = 'Tabs'
TabPanel.displayName = 'TabPanel'

export default Tabs
export type { TabsProps, TabItem, TabPanelProps } from './tabs.types'
